import { Box, Typography, useTheme } from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { RsvpResponseStatsItem } from '../../../../types/dashboard';
import { CustomChip } from '../../../../../layout/styledcomponents/CustomChip';
import GenericTable from '../../../../shared/GenericTable';
import {
    getAttendeeStatusChipTone,
    getReportChipSx,
} from '../../reportChartConfig';
import { dashboardItemsTableDefaults } from '../../dashboardReportTableConfig';

type RsvpsRespondedAttendeesListProps = {
    attendees: RsvpResponseStatsItem[];
};

const capitalize = (value?: string) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

export default function RsvpsRespondedAttendeesList({
    attendees,
}: RsvpsRespondedAttendeesListProps) {
    const theme = useTheme();

    const columns: MRT_ColumnDef<RsvpResponseStatsItem>[] = useMemo(
        () => [
            {
                accessorKey: 'attendeeName',
                header: 'Attendee',
                size: 160,
            },
            {
                accessorKey: 'email',
                header: 'Email',
                size: 180,
            },
            {
                id: 'attendeeType',
                accessorFn: (row) =>
                    row.attendeeType === 'staff' ? 'Staff' : 'External',
                header: 'Type',
                size: 100,
            },
            {
                accessorKey: 'status',
                header: 'Status',
                size: 120,
                Cell: ({ cell }) => (
                    <CustomChip
                        label={capitalize(cell.getValue<string>())}
                        sx={getReportChipSx(
                            getAttendeeStatusChipTone(cell.getValue<string>()),
                            theme,
                        )}
                        variant='outlined'
                    />
                ),
            },
            {
                id: 'respondedAt',
                accessorFn: (row) => row.respondedAt ?? null,
                header: 'Responded',
                size: 120,
                sortingFn: 'datetime',
                Cell: ({ row }) =>
                    row.original.respondedAt
                        ? moment(row.original.respondedAt).format('DD/MM/YYYY')
                        : '—',
            },
            {
                accessorKey: 'bookingId',
                header: 'Reservation',
                size: 110,
                Cell: ({ cell }) => `#${cell.getValue<number>()}`,
            },
            {
                accessorKey: 'moduleName',
                header: 'Experience',
                size: 160,
            },
            {
                accessorKey: 'guestCompany',
                header: 'Guest Company',
                size: 160,
            },
        ],
        [theme],
    );

    const rows = attendees ?? [];

    if (!rows.length) {
        return (
            <Typography sx={{ mt: 4 }} variant='body2'>
                No individual RSVP responses found for this selection.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <GenericTable
                {...dashboardItemsTableDefaults}
                columns={columns}
                data={rows}
                tableName='RSVPs Responded Report'
                paginationItemLabel='attendees'
                getRowId={(row) => String(row.id)}
            />
        </Box>
    );
}
